import { Icon, Input, InputGroup, InputLeftElement, type InputGroupProps } from '@chakra-ui/react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';

type Props = InputGroupProps & {
  placeholder?: string;
  defaultValue?: string;
  delay?: number;
  urlSync?: boolean;
  urlKey?: string;
  onCommit?: (value: string) => void;
  maxLength?: number;
};

const SearchInputWithUrlSync = forwardRef<HTMLInputElement, Props>(
  (
    {
      placeholder = 'Search',
      defaultValue = '',
      delay = 600,
      urlKey = 'search',
      onCommit,
      maxLength,
      ...styleProps
    },
    ref,
  ) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [value, setValue] = useState(searchParams.get(urlKey) ?? defaultValue);
    const hasMountedRef = useRef(false);

    useEffect(() => {
      if (!hasMountedRef.current) {
        const initialValue = searchParams.get(urlKey) ?? defaultValue;
        if (initialValue.length > 0) {
          onCommit?.(initialValue);
        }
        hasMountedRef.current = true;
      }
    }, [searchParams, urlKey, defaultValue, onCommit]);

    const handleInputChange = useDebouncedCallback((nextValue: string) => {
      onCommit?.(nextValue);

      setSearchParams(
        (prevParams) => {
          const nextParams = new URLSearchParams(prevParams);

          if (nextValue.length > 0) {
            nextParams.set(urlKey, nextValue);
          } else {
            nextParams.delete(urlKey);
          }

          const page = searchParams.get('page') ?? '';
          if (page.length > 0 && page !== '1') {
            nextParams.set('page', '1');
          }

          return nextParams;
        },
        { replace: true },
      );
    }, delay);

    return (
      <SearchInputBase
        ref={ref}
        placeholder={placeholder}
        value={value}
        onValueChange={(nextValue) => {
          setValue(nextValue);
          handleInputChange(nextValue);
        }}
        maxLength={maxLength}
        {...styleProps}
      />
    );
  },
);

SearchInputWithUrlSync.displayName = 'SearchInputWithUrlSync';

const SearchInputLocal = forwardRef<HTMLInputElement, Props>(
  (
    { placeholder = 'Search', defaultValue = '', delay = 600, onCommit, maxLength, ...styleProps },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue);

    const handleInputChange = useDebouncedCallback((nextValue: string) => {
      onCommit?.(nextValue);
    }, delay);

    return (
      <SearchInputBase
        ref={ref}
        placeholder={placeholder}
        value={value}
        onValueChange={(nextValue) => {
          setValue(nextValue);
          handleInputChange(nextValue);
        }}
        maxLength={maxLength}
        {...styleProps}
      />
    );
  },
);

SearchInputLocal.displayName = 'SearchInputLocal';

const SearchInputBase = forwardRef<
  HTMLInputElement,
  InputGroupProps & {
    placeholder?: string;
    value: string;
    onValueChange: (value: string) => void;
    maxLength?: number;
  }
>(({ placeholder, value, onValueChange, maxLength, ...styleProps }, ref) => {
  return (
    <InputGroup size="sm" {...styleProps}>
      <InputLeftElement pointerEvents="none" h="full">
        <Icon as={SearchIcon} color="fg.muted" fontSize="sm" />
      </InputLeftElement>
      <Input
        type="search"
        size="sm"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        placeholder={placeholder}
        fontSize="sm"
        ref={ref}
        autoComplete="off"
        maxLength={maxLength}
      />
    </InputGroup>
  );
});

SearchInputBase.displayName = 'SearchInputBase';

export const SearchInput = forwardRef<HTMLInputElement, Props>(
  ({ urlSync = true, ...props }, ref) => {
    if (urlSync) {
      return <SearchInputWithUrlSync ref={ref} {...props} />;
    }
    return <SearchInputLocal ref={ref} {...props} />;
  },
);

SearchInput.displayName = 'SearchInput';
